import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getActorDisplayName } from '@safetyhub/shared';
import { Button, Card, Modal } from '@safetyhub/ui';
import api from '../lib/api';

// 타입 정의
interface FeedbackSubmitter {
  userId: string;
  userName: string;
  organizationId: string | null;
  organizationName: string | null;
  createdAt: string;
}

interface GroupedFeedback {
  groupKey: string;
  type: string;
  category: string | null;
  subCategory: string | null; // 중주제 (예: "고소작업 중 추락")
  measure: string | null; // 법규 피드백의 경우 연관 대책
  description: string;
  feedbackCount: number;
  organizationCount: number;
  submitters: FeedbackSubmitter[];
  latestStatus: string;
  latestPriority: string;
  firstCreatedAt: string;
  lastCreatedAt: string;
  feedbackIds: string[];
}

interface FeedbackStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

interface PaginatedGroupedFeedbacks {
  items: GroupedFeedback[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface GroupDetailResponse {
  group: GroupedFeedback;
  feedbacks: Array<{
    id: string;
    type: string;
    category: string | null;
    description: string | null;
    metadata: Record<string, unknown> | null;
    status: string;
    priority: string;
    adminNote: string | null;
    user: { id: string; deletedAt?: string | null; member: { name: string } | null };
    organization: { id: string; name: string } | null;
    createdAt: string;
  }>;
}

// 상수
const FEEDBACK_TYPES: Record<string, string> = {
  MEASURE_LEGAL_ADD: '법령 추가',
  MEASURE_LEGAL_REMOVE: '법령 삭제',
  MEASURE_SUGGEST: '대책 제안',
  MEASURE_ADD: '대책 추가',
  MEASURE_REMOVE: '대책 삭제',
  BUG_REPORT: '버그 신고',
  FEATURE_REQUEST: '기능 요청',
  GENERAL: '일반 피드백',
};

const FEEDBACK_STATUS: Record<string, { label: string; color: string }> = {
  PENDING: { label: '대기', color: 'bg-yellow-100 text-yellow-800' },
  REVIEWING: { label: '검토중', color: 'bg-blue-100 text-blue-800' },
  ACCEPTED: { label: '수락', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: '거절', color: 'bg-red-100 text-red-800' },
  RESOLVED: { label: '완료', color: 'bg-gray-100 text-gray-800' },
};

// FEEDBACK_PRIORITY는 현재 그룹화된 뷰에서는 사용하지 않음
// 추후 개별 피드백 뷰에서 사용 가능

export default function Feedback() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    priority: '',
    page: 1,
    limit: 20,
  });
  const [selectedGroup, setSelectedGroup] = useState<GroupedFeedback | null>(null);
  const [groupDetail, setGroupDetail] = useState<GroupDetailResponse | null>(null);
  const [editForm, setEditForm] = useState({
    status: '',
    adminNote: '',
  });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    actionType: 'add' | 'remove' | 'legal';
    isDestructive: boolean;
  } | null>(null);

  // 그룹화된 피드백 목록 조회
  const { data, isLoading } = useQuery<PaginatedGroupedFeedbacks>({
    queryKey: ['admin', 'feedback', 'grouped', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.type) params.set('type', filters.type);
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      params.set('page', String(filters.page));
      params.set('limit', String(filters.limit));

      const response = await api.get(`/admin/feedback/grouped?${params}`);
      return response.data.data;
    },
  });

  // 피드백 통계 조회
  const { data: stats } = useQuery<FeedbackStats>({
    queryKey: ['admin', 'feedback', 'stats'],
    queryFn: async () => {
      const response = await api.get('/admin/feedback/stats');
      return response.data.data;
    },
  });

  // 그룹 상세 조회
  const fetchGroupDetail = async (groupKey: string) => {
    const response = await api.get(`/admin/feedback/group/${encodeURIComponent(groupKey)}`);
    return response.data.data as GroupDetailResponse;
  };

  // 그룹 상태 일괄 업데이트
  const updateGroupMutation = useMutation({
    mutationFn: async ({ groupKey, status, adminNote }: { groupKey: string; status: string; adminNote?: string }) => {
      await api.patch(`/admin/feedback/group/${encodeURIComponent(groupKey)}/status`, { status, adminNote });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      setSelectedGroup(null);
      setGroupDetail(null);
    },
  });

  // 그룹 피드백을 지식베이스에 반영 (추가)
  const applyKnowledgeMutation = useMutation({
    mutationFn: async ({ groupKey, subCategory, confidence }: { groupKey: string; subCategory?: string; confidence?: number }) => {
      const response = await api.post(`/admin/feedback/group/${encodeURIComponent(groupKey)}/apply-knowledge`, { subCategory, confidence });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      alert(data.data?.message || '지식베이스에 반영되었습니다.');
      setSelectedGroup(null);
      setGroupDetail(null);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      alert(error.response?.data?.message || '지식베이스 반영 중 오류가 발생했습니다.');
    },
  });

  // 그룹 피드백을 지식베이스에 반영 (삭제/신뢰도 감소)
  const applyRemoveKnowledgeMutation = useMutation({
    mutationFn: async ({ groupKey, action, confidenceReduction }: { groupKey: string; action: 'reduce_confidence' | 'remove'; confidenceReduction?: number }) => {
      const response = await api.post(`/admin/feedback/group/${encodeURIComponent(groupKey)}/apply-knowledge-remove`, { action, confidenceReduction });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      alert(data.data?.message || '지식베이스에 반영되었습니다.');
      setSelectedGroup(null);
      setGroupDetail(null);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      alert(error.response?.data?.message || '지식베이스 반영 중 오류가 발생했습니다.');
    },
  });

  // 그룹 피드백을 지식베이스에 반영 (법규)
  const applyLegalKnowledgeMutation = useMutation({
    mutationFn: async ({ groupKey }: { groupKey: string }) => {
      const response = await api.post(`/admin/feedback/group/${encodeURIComponent(groupKey)}/apply-knowledge-legal`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      alert(data.data?.message || '지식베이스에 반영되었습니다.');
      setSelectedGroup(null);
      setGroupDetail(null);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      alert(error.response?.data?.message || '지식베이스 반영 중 오류가 발생했습니다.');
    },
  });

  // 지식베이스 반영 가능 여부 확인
  const isApplicableToKnowledge = (type: string) => {
    return ['MEASURE_ADD', 'MEASURE_SUGGEST', 'MEASURE_REMOVE', 'MEASURE_LEGAL_ADD', 'MEASURE_LEGAL_REMOVE'].includes(type);
  };

  // 추가 유형인지 확인
  const isAddType = (type: string) => {
    return ['MEASURE_ADD', 'MEASURE_SUGGEST'].includes(type);
  };

  // 법규 유형인지 확인
  const isLegalType = (type: string) => {
    return ['MEASURE_LEGAL_ADD', 'MEASURE_LEGAL_REMOVE'].includes(type);
  };

  // 확인 모달에서 반영 실행
  const handleConfirmApply = () => {
    if (!confirmModal || !selectedGroup) return;

    switch (confirmModal.actionType) {
      case 'add':
        applyKnowledgeMutation.mutate({ groupKey: selectedGroup.groupKey });
        break;
      case 'remove':
        applyRemoveKnowledgeMutation.mutate({ groupKey: selectedGroup.groupKey, action: 'remove' });
        break;
      case 'legal':
        applyLegalKnowledgeMutation.mutate({ groupKey: selectedGroup.groupKey });
        break;
    }
    setConfirmModal(null);
  };

  // 지식베이스 반영 버튼 클릭
  const handleApplyClick = (actionType: 'add' | 'remove' | 'legal') => {
    if (!selectedGroup) return;

    const { type, category, measure, description, feedbackCount } = selectedGroup;
    let title = '';
    let message = '';
    let isDestructive = false;

    switch (actionType) {
      case 'add':
        title = '지식베이스에 추가';
        message = `"${category}" 카테고리에 "${description}" 대책을 추가합니다.\n\n${feedbackCount}건의 피드백이 완료 처리됩니다.`;
        break;
      case 'remove':
        title = '지식베이스에서 삭제';
        message = `"${category}" 카테고리에서 "${description}" 대책을 삭제합니다.\n\n${feedbackCount}건의 피드백이 완료 처리됩니다.`;
        isDestructive = true;
        break;
      case 'legal': {
        const legalAction = type === 'MEASURE_LEGAL_ADD' ? '추가' : '삭제';
        title = `법규 ${legalAction}`;
        message = `"${category}" 카테고리의 "${measure}" 대책에 "${description}" 법규를 ${legalAction}합니다.\n\n${feedbackCount}건의 피드백이 완료 처리됩니다.`;
        isDestructive = type === 'MEASURE_LEGAL_REMOVE';
        break;
      }
    }

    setConfirmModal({ isOpen: true, title, message, actionType, isDestructive });
  };

  // 피드백 의미 설명 생성
  const getFeedbackMeaning = (group: GroupedFeedback): { meaning: string; action: string; impact: string } => {
    const { type, category, subCategory, measure, description, feedbackCount } = group;
    const countText = `${feedbackCount}명의 사용자`;
    // 카테고리 표시: "떨어짐 > 고소작업 중 추락" 형태
    const categoryText = subCategory ? `${category} > ${subCategory}` : category;

    switch (type) {
      case 'MEASURE_ADD':
      case 'MEASURE_SUGGEST':
        return {
          meaning: `"${categoryText}" 위험 상황에서 AI가 "${description}" 대책을 추천하지 않았으나, ${countText}가 이 대책을 직접 추가했습니다.`,
          action: '지식베이스에 추가하면 이후 AI가 해당 카테고리에서 이 대책을 추천합니다.',
          impact: '✓ 동일한 피드백이 더 이상 발생하지 않습니다.',
        };
      case 'MEASURE_REMOVE':
        return {
          meaning: `"${categoryText}" 위험 상황에서 AI가 "${description}" 대책을 추천했으나, ${countText}가 이 대책을 삭제했습니다.`,
          action: '지식베이스에서 삭제하면 이후 AI가 해당 카테고리에서 이 대책을 추천하지 않습니다.',
          impact: '✓ 불필요한 대책 추천이 줄어듭니다.',
        };
      case 'MEASURE_LEGAL_ADD':
        return {
          meaning: `"${categoryText}" 위험의 "${measure}" 대책에 "${description}" 법규가 누락되어, ${countText}가 직접 추가했습니다.`,
          action: '지식베이스에 반영하면 이후 AI가 해당 대책과 함께 이 법규를 제시합니다.',
          impact: '✓ 법적 근거가 더 완전해집니다.',
        };
      case 'MEASURE_LEGAL_REMOVE':
        return {
          meaning: `"${categoryText}" 위험의 "${measure}" 대책에서 "${description}" 법규가 부적절하여, ${countText}가 삭제했습니다.`,
          action: '지식베이스에서 삭제하면 이후 AI가 해당 대책에 이 법규를 연결하지 않습니다.',
          impact: '✓ 잘못된 법적 근거 연결이 수정됩니다.',
        };
      default:
        return {
          meaning: `${countText}가 피드백을 제출했습니다.`,
          action: '관리자가 검토 후 조치를 결정합니다.',
          impact: '',
        };
    }
  };

  // 그룹 선택 핸들러
  const handleSelectGroup = async (group: GroupedFeedback) => {
    setSelectedGroup(group);
    setEditForm({
      status: group.latestStatus,
      adminNote: '',
    });

    try {
      const detail = await fetchGroupDetail(group.groupKey);
      setGroupDetail(detail);
    } catch (error) {
      console.error('그룹 상세 조회 실패:', error);
    }
  };

  // 그룹 상태 업데이트 핸들러
  const handleUpdateGroup = () => {
    if (!selectedGroup) return;
    updateGroupMutation.mutate({
      groupKey: selectedGroup.groupKey,
      status: editForm.status,
      adminNote: editForm.adminNote || undefined,
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">사용자 피드백</h1>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card radius="lg">
            <div className="text-sm text-gray-500">전체</div>
            <div className="text-2xl font-bold">{stats.total ?? 0}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">대기중</div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.byStatus?.['PENDING'] ?? 0}
            </div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">검토중</div>
            <div className="text-2xl font-bold text-blue-600">
              {stats.byStatus?.['REVIEWING'] ?? 0}
            </div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">완료</div>
            <div className="text-2xl font-bold text-green-600">
              {stats.byStatus?.['RESOLVED'] ?? 0}
            </div>
          </Card>
        </div>
      )}

      {/* 필터 */}
      <Card radius="lg">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
            className="border rounded px-3 py-2"
          >
            <option value="">모든 유형</option>
            {Object.entries(FEEDBACK_TYPES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="border rounded px-3 py-2"
          >
            <option value="">모든 상태</option>
            {Object.entries(FEEDBACK_STATUS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* 그룹화된 피드백 목록 */}
      <Card padding="none" radius="lg" className="overflow-x-auto">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">로딩중...</div>
        ) : !data?.items?.length ? (
          <div className="p-8 text-center text-gray-500">피드백이 없습니다.</div>
        ) : (
          <>
            <table className="min-w-[700px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-24">유형</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">내용</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-20">피드백</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-16">조직</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-20">상태</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-24">기간</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.items.map((group) => (
                  <tr
                    key={group.groupKey}
                    onClick={() => handleSelectGroup(group)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        group.type.includes('REMOVE') ? 'bg-red-50 text-red-700' :
                        group.type.includes('ADD') ? 'bg-green-50 text-green-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {FEEDBACK_TYPES[group.type] || group.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="min-w-[200px] max-w-md">
                        <div className="font-medium truncate">{group.description || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {group.category}
                          {group.subCategory && <span> &gt; {group.subCategory}</span>}
                          {group.measure && <span className="ml-1">→ {group.measure}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                        group.feedbackCount >= 5 ? 'bg-red-100 text-red-700' :
                        group.feedbackCount >= 3 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {group.feedbackCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-600">
                      {group.organizationCount}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${FEEDBACK_STATUS[group.latestStatus]?.color}`}>
                        {FEEDBACK_STATUS[group.latestStatus]?.label || group.latestStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div>{formatShortDate(group.firstCreatedAt)}</div>
                      {formatShortDate(group.firstCreatedAt) !== formatShortDate(group.lastCreatedAt) && (
                        <div className="text-xs">~ {formatShortDate(group.lastCreatedAt)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 페이지네이션 */}
            {data.totalPages > 1 && (
              <div className="px-6 py-4 flex justify-between items-center border-t">
                <div className="text-sm text-gray-500">
                  총 {data.total}개 그룹
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={filters.page === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    이전
                  </button>
                  <span className="px-3 py-1">{data.page} / {data.totalPages}</span>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={filters.page >= data.totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* 그룹 상세 모달 */}
      {selectedGroup && (
        // 액션 줄은 종전처럼 본문 끝에 둔다(패널 스크롤 안에 있던 자리 — 반영 버튼 툴팁이 그 줄에 붙어 있다).
        <Modal
          open
          onClose={() => { setSelectedGroup(null); setGroupDetail(null); }}
          title={
            <span className="flex items-center gap-3">
              피드백 그룹 상세
              {isApplicableToKnowledge(selectedGroup.type) && (
                <span className="px-2 py-1 text-xs font-normal bg-green-100 text-green-700 rounded-full">
                  지식베이스 반영 가능
                </span>
              )}
            </span>
          }
          ariaLabel="피드백 그룹 상세"
          size="xl"
          panelClassName="!max-w-3xl"
        >
            <div className="space-y-6">
              {/* 그룹 요약 */}
              <div className="bg-gray-50 p-4 rounded-card">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{selectedGroup.feedbackCount}</div>
                    <div className="text-sm text-gray-500">피드백 수</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{selectedGroup.organizationCount}</div>
                    <div className="text-sm text-gray-500">조직 수</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{selectedGroup.submitters.length}</div>
                    <div className="text-sm text-gray-500">제출자 수</div>
                  </div>
                  <div>
                    <div className="text-lg font-medium">{FEEDBACK_TYPES[selectedGroup.type]}</div>
                    <div className="text-sm text-gray-500">유형</div>
                  </div>
                </div>
              </div>

              {/* 피드백 의미 설명 */}
              {(() => {
                const { meaning, action, impact } = getFeedbackMeaning(selectedGroup);
                return (
                  <div className="bg-brand-50 border border-brand-200 rounded-card p-4 space-y-3">
                    <div>
                      <div className="text-sm font-medium text-brand-700 mb-1">📋 현황</div>
                      <div className="text-sm text-brand-800">{meaning}</div>
                    </div>
                    {isApplicableToKnowledge(selectedGroup.type) && (
                      <>
                        <div>
                          <div className="text-sm font-medium text-brand-700 mb-1">🔧 반영 시</div>
                          <div className="text-sm text-brand-800">{action}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-green-700">{impact}</div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* 피드백 상세 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-gray-500 mb-1">카테고리</div>
                  <div className="font-medium">
                    {selectedGroup.category || '-'}
                    {selectedGroup.subCategory && <span className="text-gray-600"> &gt; {selectedGroup.subCategory}</span>}
                  </div>
                </div>
                {selectedGroup.measure && (
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-gray-500 mb-1">대책</div>
                    <div className="font-medium">{selectedGroup.measure}</div>
                  </div>
                )}
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-gray-500 mb-1">{isLegalType(selectedGroup.type) ? '법규' : '내용'}</div>
                  <div className="font-medium">{selectedGroup.description || '-'}</div>
                </div>
              </div>

              {/* 제출자 목록 */}
              <div>
                <label className="text-sm font-medium text-gray-500">제출자 목록</label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {selectedGroup.submitters.map((submitter) => (
                    <div key={submitter.userId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium">{submitter.userName}</span>
                        {submitter.organizationName && (
                          <span className="text-sm text-gray-500 ml-2">({submitter.organizationName})</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(submitter.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 개별 피드백 목록 */}
              {groupDetail && (
                <div>
                  <label className="text-sm font-medium text-gray-500">상세 피드백 ({groupDetail.feedbacks.length}건)</label>
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                    {groupDetail.feedbacks.map((feedback) => (
                      <div key={feedback.id} className="p-3 border rounded-card text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-medium">{getActorDisplayName({ name: feedback.user.member?.name, deletedAt: feedback.user.deletedAt }, '-')}</span>
                            {feedback.organization && (
                              <span className="text-gray-500 ml-1">({feedback.organization.name})</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded-full ${FEEDBACK_STATUS[feedback.status]?.color}`}>
                              {FEEDBACK_STATUS[feedback.status]?.label}
                            </span>
                            <span className="text-gray-500 text-xs">{formatDate(feedback.createdAt)}</span>
                          </div>
                        </div>
                        {(() => {
                          const inspectionId = feedback.metadata?.inspectionId;
                          if (!inspectionId) return null;
                          return (
                            <div className="mt-2 text-xs text-gray-500">
                              <span>점검 ID: {String(inspectionId).slice(0, 8)}...</span>
                            </div>
                          );
                        })()}
                        {feedback.adminNote && (
                          <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                            <span className="text-yellow-700">관리자 메모: {feedback.adminNote}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 일반 피드백(버그, 기능요청 등)만 상태 변경 UI 표시 */}
              {!isApplicableToKnowledge(selectedGroup.type) && (
                <>
                  <hr />
                  <div>
                    <label className="text-sm font-medium text-gray-500">상태 변경</label>
                    <div className="mt-2 grid grid-cols-2 gap-4">
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="border rounded px-3 py-2"
                      >
                        {Object.entries(FEEDBACK_STATUS).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={editForm.adminNote}
                        onChange={(e) => setEditForm({ ...editForm, adminNote: e.target.value })}
                        placeholder="관리자 메모 (선택)"
                        className="border rounded px-3 py-2"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end gap-3">
              <button
                onClick={() => { setSelectedGroup(null); setGroupDetail(null); }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                닫기
              </button>

              {/* 지식베이스 반영 가능 유형: 반영 버튼 */}
              {isApplicableToKnowledge(selectedGroup.type) && selectedGroup.category && (
                <>
                  {/* 추가 유형 */}
                  {isAddType(selectedGroup.type) && (
                    <div className="relative group">
                      <Button
                        variant="success"
                        onClick={() => handleApplyClick('add')}
                        disabled={applyKnowledgeMutation.isPending || selectedGroup.latestStatus === 'RESOLVED'}
                      >
                        {applyKnowledgeMutation.isPending ? '반영중...' : '지식베이스에 반영'}
                      </Button>
                      {selectedGroup.latestStatus === 'RESOLVED' && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                          이미 반영 완료된 피드백입니다
                        </div>
                      )}
                    </div>
                  )}

                  {/* 삭제 유형 */}
                  {selectedGroup.type === 'MEASURE_REMOVE' && (
                    <div className="relative group">
                      <Button
                        variant="danger"
                        onClick={() => handleApplyClick('remove')}
                        disabled={applyRemoveKnowledgeMutation.isPending || selectedGroup.latestStatus === 'RESOLVED'}
                      >
                        {applyRemoveKnowledgeMutation.isPending ? '처리중...' : '지식베이스에서 삭제'}
                      </Button>
                      {selectedGroup.latestStatus === 'RESOLVED' && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                          이미 반영 완료된 피드백입니다
                        </div>
                      )}
                    </div>
                  )}

                  {/* 법규 유형 */}
                  {isLegalType(selectedGroup.type) && (
                    <div className="relative group">
                      <button
                        onClick={() => handleApplyClick('legal')}
                        disabled={applyLegalKnowledgeMutation.isPending || selectedGroup.latestStatus === 'RESOLVED'}
                        className={`px-4 py-2 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                          selectedGroup.type === 'MEASURE_LEGAL_ADD'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {applyLegalKnowledgeMutation.isPending
                          ? '반영중...'
                          : selectedGroup.type === 'MEASURE_LEGAL_ADD'
                            ? '지식베이스에 반영'
                            : '지식베이스에서 삭제'}
                      </button>
                      {selectedGroup.latestStatus === 'RESOLVED' && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                          이미 반영 완료된 피드백입니다
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* 일반 피드백: 상태 변경 버튼 */}
              {!isApplicableToKnowledge(selectedGroup.type) && (
                <Button
                  variant="primary"
                  onClick={handleUpdateGroup}
                  disabled={updateGroupMutation.isPending}
                >
                  {updateGroupMutation.isPending ? '저장중...' : '상태 변경'}
                </Button>
              )}
            </div>
        </Modal>
      )}

      {/* 지식베이스 반영 확인 모달 — 그룹 상세 위에 겹쳐 뜨는 서브 모달이라 한 층 위(z-[80]) */}
      {confirmModal?.isOpen && (
        <Modal
          open
          onClose={() => setConfirmModal(null)}
          ariaLabel={confirmModal.title}
          size="md"
          overlayZClassName="z-[80]"
          footer={
            <>
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                취소
              </button>
              <button
                onClick={handleConfirmApply}
                className={`px-4 py-2 text-white rounded ${
                  confirmModal.isDestructive
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                반영하기
              </button>
            </>
          }
        >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  confirmModal.isDestructive ? 'bg-red-100' : 'bg-green-100'
                }`}>
                  {confirmModal.isDestructive ? (
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <h3 className="text-lg font-bold">{confirmModal.title}</h3>
              </div>

              <div className="text-gray-600 whitespace-pre-line mb-4">
                {confirmModal.message}
              </div>

              <div className={`p-3 rounded-card text-sm ${
                confirmModal.isDestructive ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
              }`}>
                <div className="font-medium mb-1">⚠️ 주의</div>
                <div>이 작업은 되돌릴 수 없습니다. 지식베이스가 즉시 변경됩니다.</div>
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
}
